import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calendar,
  MapPin,
  ArrowRight,
  Star,
  Users,
  // ExternalLink,
} from "react-feather";
import LaunchCompletedBadge from "./launch/LaunchCompletedBadge";
import {
  LAUNCH_EVENT,
  LAUNCH_HIGHLIGHTS,
  LAUNCH_MILESTONES,
  LAUNCH_GUESTS,
} from "../../data/launchEventConfig";
import rudraLogo from "../../assets/images/rudra-ride-logo.png";
import eduLogo from "../../assets/images/edu-pravahaa-logo.png";
// import MagneticButton from "./hero/MagneticButton";

const logos = { "Rudra Ride": rudraLogo, "Edu Pravahaa": eduLogo };

export default function LaunchEventSection() {
  return (
    <section id="launch-event" className="launch-event-section py-24 md:py-32">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#2563EB]/15 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#22c55e]/10 blur-[100px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14 lg:mb-16"
        >
          <LaunchCompletedBadge className="mb-5" />
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
            Grand Launch Event{" "}
            <span className="premium-heading-gradient">2026</span>
          </h2>
          <p className="mt-5 text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            Rudra Ride and Edu Pravahaa were officially launched at Hotel Regalia, Tirupathi —
            marking a new chapter in smart mobility and digital education.
          </p>
        </motion.div>

        {/* Milestone timeline */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="launch-recap-card mb-12 overflow-hidden"
        >
          <div className="launch-milestone-track">
            {LAUNCH_MILESTONES.map((m, i) => (
              <div key={m.step} className="launch-milestone-item">
                <p className="launch-milestone-step">{m.step}</p>
                <p className="text-white font-bold mt-2">{m.label}</p>
                <p className="text-white/50 text-sm mt-1">{m.detail}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Product highlights */}
        <div className="grid md:grid-cols-2 gap-6 mb-14">
          {LAUNCH_HIGHLIGHTS.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="launch-recap-card p-6 sm:p-8 group"
              style={{ borderColor: `${item.accent}33` }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="flex-shrink-0 rounded-xl bg-white p-4 shadow-lg w-fit">
                  <img
                    src={logos[item.title]}
                    alt={item.title}
                    className="h-16 w-auto object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Star size={16} style={{ color: item.accent }} />
                    <span
                      className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: item.accent }}
                    >
                      Launched at Event
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{item.title}</h3>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">{item.desc}</p>
                  <Link
                    to={item.link}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold transition-colors group-hover:gap-3"
                    style={{ color: item.accent }}
                  >
                    Explore platform <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Event details */}
        <div className="grid sm:grid-cols-3 gap-5 mb-12">
          {[
            {
              icon: Calendar,
              label: "Event Date",
              value: LAUNCH_EVENT.date,
              sub: `${LAUNCH_EVENT.day}, ${LAUNCH_EVENT.time}`,
            },
            {
              icon: MapPin,
              label: "Venue",
              value: LAUNCH_EVENT.venue,
              sub: "Launch ceremony location",
            },
            {
              icon: MapPin,
              label: "Address",
              value: LAUNCH_EVENT.address.split(",")[0],
              sub: "Renugunta Road, Tirupathi",
            },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="launch-detail-card"
            >
              <item.icon className="text-[#F5C044] mb-3" size={22} />
              <p className="text-white/45 text-xs uppercase tracking-wider font-semibold">
                {item.label}
              </p>
              <p className="text-white font-bold text-lg mt-1">{item.value}</p>
              <p className="text-white/50 text-sm mt-1">{item.sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Guests */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold mb-4 flex items-center justify-center gap-2">
            <Users size={14} /> Distinguished attendees
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {LAUNCH_GUESTS.map((g) => (
              <span key={g} className="launch-guest-chip">
                {g}
              </span>
            ))}
          </div>
        </motion.div>

        {/* CTAs — post-launch */}
        {/* <div className="flex flex-wrap justify-center gap-4">
          <MagneticButton to="/edupravahaa" variant="gold">
            Explore Edu Pravahaa
          </MagneticButton>
          <MagneticButton to="/rudra-rides" variant="primary">
            Explore Rudra Ride
          </MagneticButton>
          <MagneticButton href={LAUNCH_EVENT.mapUrl} variant="glass">
            <ExternalLink size={18} /> Event Venue
          </MagneticButton>
        </div> */}
      </div>
    </section>
  );
}
